import axios from 'axios';
import router from '@/router/index';
import { API_URL } from '@/config';

const API_BASE_URL = API_URL;
const CACHE_VERSION = '1.4'; // Incrementado por cambios de seguridad
const LOCALE_STORAGE_KEY = 'godea_locale';

const getCurrentLocale = () => {
    try {
        return localStorage.getItem(LOCALE_STORAGE_KEY) === 'en' ? 'en' : 'es';
    } catch {
        return 'es';
    }
};

// Define different timeout settings for different types of requests
const timeoutSettings = {
    default: 30000,           // 30 seconds default
    product: 15000,           // 15 seconds for individual product requests
    productsList: 25000,      // 25 seconds for products list
    auth: 15000,              // 15 seconds for authentication requests
    register: 40000,          // 40 seconds for registration (creates user + sends email)
    orders: 20000,            // 20 seconds for orders
    upload: 60000,            // 60 seconds for uploads
};

// Estado global de autenticación verificado por el servidor
const authState = {
    isVerified: false,
    isAdmin: false,
    lastVerified: null,
    isRefreshing: false,      // Flag para evitar múltiples refresh simultáneos
    refreshPromise: null      // Promise compartida para refresh en curso
};

// =====================================================
// SISTEMA DE REFRESH DE TOKENS JWT
// =====================================================
// Los tokens ahora expiran en 1 hora. Este sistema refresca
// automáticamente el token antes de que expire.

/**
 * Refresca el token de acceso usando el refresh token
 * @returns {Promise<string|null>} Nuevo access token o null si falla
 */
const refreshAccessToken = async () => {
    // Si ya hay un refresh en curso, esperar a que termine
    if (authState.isRefreshing && authState.refreshPromise) {
        return authState.refreshPromise;
    }
    
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
        return null;
    }
    
    authState.isRefreshing = true;
    
    authState.refreshPromise = (async () => {
        try {
            const response = await axios.post(`${API_BASE_URL}/auth/token/refresh/`, {
                refresh: refreshToken
            }, {
                timeout: 10000
            });
            
            if (response.data.access) {
                // Guardar nuevo access token
                localStorage.setItem('accessToken', response.data.access);
                
                // Si también retorna un nuevo refresh token (rotación), guardarlo
                if (response.data.refresh) {
                    localStorage.setItem('refreshToken', response.data.refresh);
                }
                
                return response.data.access;
            }
            
            throw new Error('No se recibió nuevo token');
        } catch (error) {
            console.error('[Auth] Error al refrescar token:', error);
            
            // Si el refresh token también expiró, limpiar sesión
            if (error.response?.status === 401) {
                clearAuthState();
                router.push({ name: 'LoginForm', query: { expired: 'true' } });
            }
            
            return null;
        } finally {
            authState.isRefreshing = false;
            authState.refreshPromise = null;
        }
    })();
    
    return authState.refreshPromise;
};

/**
 * Verifica si el token JWT está próximo a expirar
 * @param {string} token - Token JWT
 * @param {number} bufferMinutes - Minutos antes de expiración para considerar "próximo a expirar"
 * @returns {boolean}
 */
const isTokenExpiringSoon = (token, bufferMinutes = 5) => {
    if (!token) return true;
    
    try {
        // Decodificar el payload del JWT (base64)
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expirationTime = payload.exp * 1000; // Convertir a milisegundos
        const now = Date.now();
        const bufferMs = bufferMinutes * 60 * 1000;
        
        // Retornar true si expira en menos de bufferMinutes
        return (expirationTime - now) < bufferMs;
    } catch (error) {
        console.error('[Auth] Error decodificando token:', error);
        return true; // Asumir expirado si no se puede decodificar
    }
};

// Función para verificar el estado del usuario con el servidor
const verifyAuthState = async (force = false) => {
    // Verificar solo una vez cada 5 minutos a menos que se fuerce
    const now = new Date();
    if (!force && authState.lastVerified && (now - authState.lastVerified) < 5 * 60 * 1000) {
        return authState;
    }
    
    const token = localStorage.getItem('accessToken');
    if (!token) {
        authState.isVerified = false;
        authState.isAdmin = false;
        localStorage.setItem('isAdmin', 'false'); // Asegurarse de limpiar el estado
        return authState;
    }

    try {
        // Usar endpoint específico para verificación de admin
        const response = await axios.get(`${API_BASE_URL}/auth/verify-admin/`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000 // Timeout corto para evitar esperas largas
        });
        
        authState.isVerified = true;
        authState.isAdmin = response.data.is_admin === true;
        authState.lastVerified = now;
        
        // CRÍTICO: Verificación detallada y depuración
        
        // Asegurar que sea un booleano verdadero
        authState.isAdmin = response.data.is_admin === true;
        
        // Actualizar el estado en localStorage con conversión explícita
        localStorage.setItem('isAdmin', authState.isAdmin ? 'true' : 'false');
        
        // Actualizar customer_type si el endpoint lo devuelve
        if (response.data.customer_type) {
            localStorage.setItem('customerType', response.data.customer_type);
        }
            
        // Si es admin, desactivar mantenimiento para este usuario
        if (authState.isAdmin) {
            localStorage.setItem('maintenance_mode', 'false');
        }
            
        // Reiniciar caché de mantenimiento
        try {
            const maintenanceService = (await import('@/services/maintenanceService')).default;
            await maintenanceService.resetMaintenanceCache(false);
        } catch (e) {
            console.error('Error al reiniciar cache de mantenimiento:', e);
        }
        
        return authState;
    } catch (error) {
        console.error('Error verificando estado de autenticación:', error);
        authState.isVerified = false;
        authState.isAdmin = false;
        return authState;
    }
};

// Limpiar caché antiguo si es necesario
const initializeCache = () => {
    const storedVersion = localStorage.getItem('cacheVersion');
    if (storedVersion !== CACHE_VERSION) {
        // Agregar limpieza de caché de actividades
        Object.keys(localStorage).forEach(key => {
            if (key.startsWith('activities_') || key === 'activity_stats') {
                localStorage.removeItem(key);
                localStorage.removeItem(`${key}_timestamp`);
            }
        });
        // Limpiar caché relacionado pero preservar el token de acceso
        localStorage.removeItem('products');
        localStorage.removeItem('productsTimestamp');
        localStorage.removeItem('userOrders');
        localStorage.removeItem('adminOrders');
        localStorage.removeItem('ordersTimestamp');
        // Establecer nueva versión
        localStorage.setItem('cacheVersion', CACHE_VERSION);
    }
};

// Ejecutar limpieza al iniciar
initializeCache();

/**
 * Helper to determine appropriate timeout based on the request URL and method
 * @param {string} url - The request URL
 * @param {string} method - The HTTP method (get, post, etc.)
 * @returns {number} The timeout value in milliseconds
 */
const determineTimeout = (url = '', method = 'get') => {
    if (!url) return timeoutSettings.default;
    
    const lowercaseUrl = url.toLowerCase();
    const isUpload = method.toLowerCase() === 'post' && (
        lowercaseUrl.includes('/image') || 
        lowercaseUrl.includes('/upload') || 
        lowercaseUrl.includes('/file')
    );
    
    if (isUpload) return timeoutSettings.upload;
    if (lowercaseUrl.includes('/products/') && !lowercaseUrl.endsWith('/products/')) return timeoutSettings.product;
    if (lowercaseUrl.includes('/products')) return timeoutSettings.productsList;
    if (lowercaseUrl.includes('/auth/register')) return timeoutSettings.register;
    if (lowercaseUrl.includes('/auth/') || lowercaseUrl.includes('/login')) return timeoutSettings.auth;
    if (lowercaseUrl.includes('/orders/') || lowercaseUrl.includes('/order/')) return timeoutSettings.orders;
    
    return timeoutSettings.default;
};

const extractApiErrorMessage = (data, fallback) => {
    if (!data) return fallback;
    if (typeof data === 'string') return data;

    const knownMessage = data.message || data.error || data.detail || data.reason;
    if (Array.isArray(knownMessage)) return knownMessage.join(' ');
    if (typeof knownMessage === 'string') return knownMessage;

    if (typeof data === 'object') {
        const firstValue = Object.values(data).find(value => value);
        if (Array.isArray(firstValue)) return firstValue.join(' ');
        if (typeof firstValue === 'string') return firstValue;
    }

    return fallback;
};

export const normalizeApiError = (error, fallbackMessage = 'Ocurrio un error al procesar la solicitud') => {
    if (error?.isApiError) return error;

    const response = error?.response;
    const data = response?.data;
    const status = response?.status || error?.status || 0;
    const message = extractApiErrorMessage(data, error?.message || fallbackMessage);
    const code = data?.code || data?.error_code || error?.code || null;

    return {
        message,
        status,
        code,
        details: data || error?.details || null,
        response,
        config: error?.config,
        request: error?.request,
        originalError: error?.originalError || error,
        isApiError: true,
        isTimeout: Boolean(error?.isTimeout || error?.code === 'ECONNABORTED'),
        isNetworkError: Boolean(error?.isNetworkError || error?.message === 'Network Error')
    };
};

const apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: timeoutSettings.default,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
    }
});

// =====================================================
// INTERCEPTOR 1: Token refresh automático antes de requests
// =====================================================
apiClient.interceptors.request.use(
    async config => {
        config.headers = config.headers || {};
        config.headers['Accept-Language'] = getCurrentLocale();

        // Excluir endpoints de autenticación para evitar loops
        const isAuthEndpoint = config.url?.includes('/auth/login') || 
                               config.url?.includes('/auth/register') ||
                               config.url?.includes('/auth/token/refresh');
        
        if (!isAuthEndpoint) {
            const token = localStorage.getItem('accessToken');
            
            // Si hay token y está próximo a expirar, refrescarlo
            if (token && isTokenExpiringSoon(token, 5)) {
                const newToken = await refreshAccessToken();
                
                if (newToken) {
                    config.headers.Authorization = `Bearer ${newToken}`;
                } else if (token) {
                    // Si no se pudo refrescar pero hay token, usarlo de todos modos
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } else if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        
        // Set appropriate timeout based on the request URL if not explicitly set
        if (!config.timeoutSet) {
            config.timeout = determineTimeout(config.url, config.method);
        }

        return config;
    },
    error => {
        return Promise.reject(normalizeApiError(error));
    }
);

// =====================================================
// INTERCEPTOR 2: Proteger rutas administrativas
// =====================================================
apiClient.interceptors.request.use(
    async config => {
        const url = config.url || '';
        const isSiteConfigPublic = url.includes('/site-config/public');
        // Verificar el estado de administrador para rutas administrativas
        if ((url.includes('/admin/') || 
            (url.includes('/site-config/') && !isSiteConfigPublic) ||
            url.includes('/activity-logs/') ||
            url.includes('/orders/') && 
            (config.method === 'patch' || 
             url.includes('/stats') || 
             url.includes('/pending') || 
             url.includes('/archived') || 
             url.includes('/items') || 
             url.includes('/invoice') || 
             url.includes('/notify_shipping') || 
             url.includes('/archive') || 
             url.includes('/unarchive'))
        )) {
            await verifyAuthState(true);
            if (!authState.isAdmin) {
                // Cancelar la solicitud si el usuario no es administrador
                console.error('Acceso denegado: intento de acceso a ruta administrativa sin permisos');
                return Promise.reject({
                    response: {
                        status: 403,
                        data: { error: 'Acceso denegado: no tienes permisos de administrador' }
                    }
                });
            }
        }
        return config;
    },
    error => Promise.reject(normalizeApiError(error))
);

// =====================================================
// INTERCEPTOR (último registrado = se ejecuta primero):
// Axios por defecto envía Content-Type: application/json; con FormData eso hace que
// transformRequest serialice el body como JSON (formDataToJSON) y se pierdan archivos.
// Quitar Content-Type aquí para que el navegador envíe multipart/form-data con boundary.
// =====================================================
apiClient.interceptors.request.use(
    (config) => {
        const data = config.data;
        if (
            data &&
            typeof FormData !== 'undefined' &&
            (data instanceof FormData ||
                (typeof data.append === 'function' && data[Symbol.toStringTag] === 'FormData'))
        ) {
            const h = config.headers;
            if (h && typeof h.set === 'function') {
                h.set('Content-Type', undefined);
            } else if (h && typeof h === 'object') {
                delete h['Content-Type'];
                delete h['content-type'];
            }
        }
        return config;
    },
    (error) => Promise.reject(normalizeApiError(error))
);

// =====================================================
// INTERCEPTOR 3: Manejo de respuestas y errores
// =====================================================
apiClient.interceptors.response.use(
    response => {
        // Si la respuesta incluye información de admin, actualizar localStorage
        if (response.data && typeof response.data.is_staff === 'boolean') {
            const isAdmin = response.data.is_staff;
            localStorage.setItem('isAdmin', isAdmin ? 'true' : 'false');
            
            // Si es admin, desactivar mantenimiento
            if (isAdmin) {
                localStorage.setItem('maintenance_mode', 'false');
            }
        }
        
        return response;
    },
    async error => {  // <-- Ahora es async para poder usar await
        // Handle connection abort errors more gracefully
        if (error.code === 'ECONNABORTED') {
            
            // Display a more helpful error message with the affected endpoint
            const url = (error.config?.url || '').toLowerCase();
            const isRegister = url.includes('/auth/register');
            const message = isRegister
                ? 'El registro tardó más de lo esperado. Si ya recibiste el email de verificación, revisá tu bandeja y continuá desde ahí. Si no llegó, volvé a intentarlo.'
                : `La solicitud tardó demasiado tiempo. Por favor, inténtalo nuevamente.`;
            return Promise.reject(normalizeApiError({
                message,
                originalError: error,
                isTimeout: true,
                code: error.code,
                config: error.config,
                request: error.request
            }, message));
        }

        // Handle network errors
        if (error.message === 'Network Error') {
            
            return Promise.reject(normalizeApiError({
                message: 'No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet.',
                originalError: error,
                isNetworkError: true,
                code: error.code,
                config: error.config,
                request: error.request
            }, 'No se pudo conectar con el servidor. Por favor, verifica tu conexion a internet.'));
        }

        // =====================================================
        // MANEJO DE TOKEN EXPIRADO (401) CON RETRY AUTOMÁTICO
        // =====================================================
        if (error.response && error.response.status === 401) {
            const originalRequest = error.config;
            
            // Evitar loops infinitos - solo reintentar una vez
            if (!originalRequest._retry) {
                originalRequest._retry = true;
                
                // Intentar refrescar el token
                const newToken = await refreshAccessToken();
                
                if (newToken) {
                    // Reintentar la petición original con el nuevo token
                    originalRequest.headers.Authorization = `Bearer ${newToken}`;
                    return apiClient(originalRequest);
                }
            }
            
            // Si no se pudo refrescar, limpiar sesión y redirigir
            clearAuthState();
            router.push({ name: 'LoginForm', query: { expired: 'true' } });
        }
        
        // Handle forbidden errors (no permission)
        if (error.response && error.response.status === 403) {
            // Corregir el estado de isAdmin en localStorage si es necesario
            localStorage.setItem('isAdmin', 'false');
            // Redirigir si estamos en una ruta administrativa
            if (router.currentRoute.value.path.includes('/configuracion')) {
                router.push('/account/orders');
            }
        }
        return Promise.reject(normalizeApiError(error));
    }
);

// Exponer funciones de autenticación
export const checkAdminStatus = async () => {
    try {
        // Verificación directa con endpoint específico - independiente del cache
        const token = localStorage.getItem('accessToken');
        if (!token) return false;
        
        // Usar endpoint especializado para verificación de admin
        const response = await axios.get(`${API_BASE_URL}/auth/verify-admin/`, {
            headers: { Authorization: `Bearer ${token}` },
            timeout: 5000 // Timeout corto para respuesta rápida
        });
        
        // Actualizar el estado global
        const isAdminStatus = response.data.is_admin === true;
        authState.isVerified = true;
        authState.isAdmin = isAdminStatus;
        authState.lastVerified = new Date();
        
        // Actualizar localStorage directamente
        localStorage.setItem('isAdmin', isAdminStatus ? 'true' : 'false');
        
        // Si es admin, desactivar mantenimiento
        if (isAdminStatus) {
            localStorage.setItem('maintenance_mode', 'false');
        }
        
        return isAdminStatus;
    } catch (error) {
        console.error('[API] Error verificando estado admin:', error);
        
        // Si hay error de red pero tenemos estado admin en localStorage
        const localAdminStatus = localStorage.getItem('isAdmin') === 'true';
        if (localAdminStatus) {
            return true;
        }
        
        return false;
    }
};

// Function to check API connectivity
export const checkApiConnection = async () => {
    try {
        const start = Date.now();
        // Use a lightweight endpoint for connection test
        const response = await axios.get(`${API_BASE_URL}/ping/`, {
            timeout: 5000 // Short timeout
        });
        const latency = Date.now() - start;
        
        return {
            connected: true,
            latency,
            status: response.status,
            message: `API connected (${latency}ms)`
        };
    } catch (error) {
        return {
            connected: false,
            latency: null,
            status: error.response?.status || 0,
            message: error.code === 'ECONNABORTED' 
                ? 'API timeout (>5s)' 
                : error.message === 'Network Error'
                    ? 'Network error' 
                    : `API error: ${error.message || 'Unknown error'}`
        };
    }
};

// Ya exportamos checkAdminStatus arriba, así que no necesitamos volver a exportarlo aquí

// Util para forzar limpieza del estado de autenticación
export const clearAuthState = () => {
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');  // También limpiar refresh token
  localStorage.removeItem('isAdmin');
  localStorage.removeItem('username');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('customerType');
  localStorage.removeItem('maintenance_mode');
  localStorage.removeItem('maintenance_bypass');
  localStorage.removeItem('maintenance_bypass_expiry');
  
  // Resetear estado global
  authState.isVerified = false;
  authState.isAdmin = false;
  authState.lastVerified = null;
  authState.isRefreshing = false;
  authState.refreshPromise = null;
};

// Exportar funciones de refresh para uso externo si es necesario
export { refreshAccessToken, isTokenExpiringSoon };

export default apiClient;
